import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { Baggage, SelectedBaggage, SelectedBaggageItem } from '../../BaggageService/types';
import { MOBILE_MIN_WIDTH, scrollTo } from '../../utils';
import { useTheme } from '../../theme';
import AdditionalServiceBanner from '../../AdditionalServiceBanner/AdditionalServiceBanner';
import SimpleLoader from '../../SimpleLoader';
import IncludedBaggage from '../../Baggage/IncludedBaggage/components/IncludedBaggage';
import { Passenger, PassengerLabels, Segment, SelectedBaggageService } from '../../Baggage/types';
import { getServicesSavingState } from '../../Checkout/store/servicesIsSaving/selectors';
import { factorySelectedUpgrades } from '../../Baggage/IncludedBaggage/utils';
import AdditionalBaggage from '../../Baggage/AdditionalBaggage/components/AdditionalBaggage';
import SpecialEquipment from '../../Baggage/SpecialEquipment';
import OverweightBaggage from '../../Baggage/OverweightBaggage';
import { WxEvents } from '../../DataLayerAnalytics/types';
import { filterIncludedBaggageByRfics } from '../../Baggage/UpgradeBaggageDialog/utils';
import WebskyAnalyticsApollo from '../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { getOrder } from '../../Checkout/store/order/selectors';
import { getSelectedServicesState } from '../../Checkout/store/selectedServices/selectors';
import AvailableServicesCountContextProvider from '../../AdditionalServices@deprecated/context';
import { useMediaQuery } from 'react-responsive';
import { MobileBaggageRules } from './MobileBaggageRules/MobileBaggageRules';
import InfoMessages from '../../InfoMessages';
import { InfoMessagePositionsEnum } from '@websky/graphql';
import { ServiceMeta, ServicePayload } from '../../Checkout/store/selectedServices/actions';
import { baggageServicesToDeletePayloadAdapter } from '../utils';

interface Props {
	passengers: Passenger[];
	passengerLabels: PassengerLabels;
	segments: Segment[];
	baggage: Baggage[];
	includedBaggage: SelectedBaggageItem[];
	upgradeBaggage: Baggage[];
	additionalBaggage: Baggage[];
	specialBaggage: Baggage[];
	overweightBaggage: Baggage[];
	selectedBaggage: SelectedBaggage[];
	simpleBaggageSelector?: boolean;
	readonly?: boolean;
	isLoading?: boolean;
	hideBanner?: boolean;
	disabled: boolean;
	dynamicBaggagePricing?: boolean;
	className?: string;
	analyticsMeta?: ServiceMeta;
	onDeleteServices: (servicesToDelete: ServicePayload[]) => void;
}

const BaggagePage: React.FC<Props> = props => {
	const theme = useTheme('BaggagePage').BaggagePage;

	const order = useSelector(getOrder);
	const selectedServicesState = useSelector(getSelectedServicesState);
	const servicesSaving = useSelector(getServicesSavingState);

	useEffect(() => {
		scrollTo(0);
		WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.BaggageList);
	}, []);

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const selectedServices = useMemo(() => {
		return factorySelectedUpgrades(props.selectedBaggage);
	}, [props.selectedBaggage]);

	const deleteSelectedBaggage = (servicesToDelete: SelectedBaggageService[]) => {
		props.onDeleteServices(baggageServicesToDeletePayloadAdapter(servicesToDelete));
	};

	const sortedAdditionalBaggage = React.useMemo(
		() => props.additionalBaggage.sort((a, b) => a.value?.amount - b.value?.amount),
		[props.additionalBaggage]
	);

	const filteredIncludedBaggageByRfics = filterIncludedBaggageByRfics(
		props.baggage,
		props.includedBaggage,
		selectedServices
	);

	const onDelete = props.readonly ? undefined : deleteSelectedBaggage;

	return (
		<div className={cn(theme.baggage, props.className)}>
			{props.isLoading && <SimpleLoader />}

			{!props.hideBanner && (isMobile ? <MobileBaggageRules /> : <AdditionalServiceBanner service="baggage" />)}

			<AvailableServicesCountContextProvider value={{ order, selectedServices: selectedServicesState }}>
				{!!order.messages.length && (
					<InfoMessages
						messages={order.messages}
						position={[InfoMessagePositionsEnum.baggage, InfoMessagePositionsEnum.baggageTop]}
						className={theme.infoMessage}
						itemHtmlClassName={theme.infoMessage__htmlMessage}
						htmlClassName={theme.infoMessage__htmlMessageContent}
					/>
				)}

				<div className={theme.items}>
					<div className={theme.newBaggage}>
						{(filteredIncludedBaggageByRfics.length > 0 || props.upgradeBaggage?.length > 0) && (
							<IncludedBaggage
								className={theme.included_baggage}
								passengers={props.passengers}
								passengerLabels={props.passengerLabels}
								segments={props.segments}
								includedBaggage={filteredIncludedBaggageByRfics}
								upgradeServices={props.upgradeBaggage}
								selectedServices={selectedServices}
								isLoading={servicesSaving}
								dynamicBaggagePricing={props.dynamicBaggagePricing}
								analyticsMeta={props.analyticsMeta}
								onDelete={onDelete}
							/>
						)}

						{sortedAdditionalBaggage.length > 0 && (
							<AdditionalBaggage
								className={theme.extra_baggage}
								additionalBaggage={sortedAdditionalBaggage}
								passengers={props.passengers}
								passengerLabels={props.passengerLabels}
								segments={props.segments}
								selectedServices={selectedServices}
								isLoading={servicesSaving}
								dynamicBaggagePricing={props.dynamicBaggagePricing}
								onChange={console.log}
								disabled={props.disabled}
								analyticsMeta={props.analyticsMeta}
								onDelete={onDelete}
							/>
						)}
					</div>
				</div>

				<div className={cn(theme.items, theme.additional)}>
					{props.overweightBaggage.length > 0 && (
						<OverweightBaggage
							className={cn(theme.additionalBaggage, theme.overweight_baggage)}
							passengers={props.passengers}
							passengerLabels={props.passengerLabels}
							segments={props.segments}
							services={props.overweightBaggage}
							selectedServices={selectedServices}
							disabled={props.disabled}
							dynamicBaggagePricing={props.dynamicBaggagePricing}
							isLoading={servicesSaving}
							analyticsMeta={props.analyticsMeta}
							onDelete={onDelete}
						/>
					)}

					{props.specialBaggage.length > 0 && (
						<SpecialEquipment
							className={cn(theme.additionalBaggage, theme.equipment_baggage)}
							passengers={props.passengers}
							passengerLabels={props.passengerLabels}
							segments={props.segments}
							services={props.specialBaggage}
							selectedServices={selectedServices}
							disabled={props.disabled}
							dynamicBaggagePricing={props.dynamicBaggagePricing}
							isLoading={servicesSaving}
							analyticsMeta={props.analyticsMeta}
							onDelete={onDelete}
						/>
					)}
				</div>
			</AvailableServicesCountContextProvider>
		</div>
	);
};

export default BaggagePage;
